import { useEffect, useState } from 'react'
import { requestPasswordReset } from 'wasp/client/auth'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '../../v32/components/ui/card'
import { Input } from '../../v32/components/ui/input'
import { Label } from '../../v32/components/ui/label'
import { Button } from '../../v32/components/ui/button'
import { track as trackAnalytics } from '../../analytics'

export function RequestPasswordResetPage(): JSX.Element {
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [sent, setSent] = useState(false)
  const [devLink, setDevLink] = useState<string | null>(null)

  useEffect(() => {
    try { trackAnalytics('auth_view', { page: 'request_password_reset' }) } catch {}
  }, [])

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const res = await requestPasswordReset({ email })
      if (res?.success) {
        setSent(true)
        try { trackAnalytics('auth_submit', { page: 'request_password_reset', success: true }) } catch {}
        if (import.meta.env.DEV) {
          // In development, Wasp may log the reset link/token to the server console.
          // Try to fetch the last reset link from server logs for convenience.
          try {
            const r = await fetch('/api/dev/last-reset-link')
            const j = await r.json()
            if (j?.ok && j?.link) setDevLink(j.link)
          } catch {}
          console.info('[Dev] If using dummy/dev email, check server logs for the reset link/token.')
        }
      } else {
        setError('Unable to send reset link. Please try again.')
      }
    } catch (err: any) {
      setError(err?.message || 'Unable to send reset link')
      try { trackAnalytics('auth_submit', { page: 'request_password_reset', success: false }) } catch {}
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-sand flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md border-0 shadow-lg rounded-lg">
          <CardHeader className="text-center pb-6">
            <div className="text-3xl font-bold text-dusk mb-2">nura</div>
            <h1 className="text-2xl font-semibold text-dusk mb-2">Reset your password</h1>
            <p className="text-muted-foreground">Enter your email to receive a reset link.</p>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="space-y-4 text-center">
                <p className="text-sm text-dusk">
                  If an account exists for <span className="font-medium">{email}</span>, a reset link has been sent.
                </p>
                {import.meta.env.DEV && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Dev note: Check server logs for the token if the link is not shown.</p>
                    {devLink && (
                      <p>
                        Last reset link detected:{' '}
                        <a className="text-warning-600 underline break-all" href={devLink}>{devLink}</a>
                      </p>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-center gap-3">
                  <Link to="/login" className="text-warning-600 hover:text-warning-700 text-sm font-medium">Back to sign in</Link>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email address</Label>
                  <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send reset link'}
                </Button>
                <div className="text-center text-sm">
                  <span className="text-muted-foreground">Remembered it? </span>
                  <Link to="/login" className="text-warning-600 hover:text-warning-700 font-medium">Sign in</Link>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
